import React, { useState, useRef, useCallback } from 'react';
import { exportVideo } from '../../utils/exporter';
import CanvasPreview from '../CanvasPreview';
import Timeline from '../Timeline';
import Inspector from '../Inspector';
import MediaBin from '../MediaBin';
import TopNavBar from '../layout/TopNavBar';
import SideNavBar from '../layout/SideNavBar';

export default function WorkspaceView() {
  const [audioUrl, setAudioUrl] = useState(null);
  const [bgMedia, setBgMedia] = useState({ type: 'color', url: '', color: '#131313', element: null, filter: 'none' });
  const [lyrics, setLyrics] = useState([]);
  const [styles, setStyles] = useState({
    fontFamily: 'Inter',
    fontSize: 64,
    textColor: '#ffffff',
    activeColor: '#c3c0ff',
    strokeEnabled: true,
    strokeColor: '#000000',
    strokeWidth: 4,
    glowEnabled: false,
    glowColor: '#c3c0ff',
    glowBlur: 10,
    visualizerEnabled: false,
    visualizerStyle: 'bars',
    animationPreset: 'pop-in',
  });
  
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  
  // Right panel tab state
  const [activeTab, setActiveTab] = useState('script'); // 'script', 'media', 'style'

  const wavesurferRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaElementSourceRef = useRef(null);
  const audioDestRef = useRef(null);
  const analyserRef = useRef(null);

  const handlePlayPause = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  }, []);

  const handleExport = async () => {
    if (!audioUrl || !canvasRef.current || !wavesurferRef.current) return;
    if (bgMedia.type === 'video' && bgMedia.element) {
      bgMedia.element.currentTime = 0;
    }
    setIsExporting(true);
    try {
      await exportVideo(
        canvasRef.current,
        wavesurferRef.current,
        audioContextRef.current,
        audioDestRef.current,
        duration,
        bgMedia,
        lyrics,
        styles
      );
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed. Check console for details.');
    }
    setIsExporting(false);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col h-full w-full">
      <TopNavBar onExport={handleExport} isExporting={isExporting} />
      
      <div className="flex-1 flex overflow-hidden">
        <SideNavBar />
        
        <main className="flex-1 flex flex-col p-unit gap-unit bg-surface-dim overflow-hidden">
          {/* Top Row: Video & Editor */}
          <div className="flex flex-1 gap-unit overflow-hidden min-h-0">
            
            {/* Video Preview Panel */}
            <section className="flex-[3] bg-surface-container border border-outline-variant rounded-lg flex flex-col overflow-hidden relative shadow-sm">
              <div className="h-10 border-b border-outline-variant bg-surface-container-low flex items-center justify-between px-3 shrink-0">
                <div className="flex items-center gap-2 text-on-surface-variant">
                  <span className="material-symbols-outlined text-[18px]">smart_display</span>
                  <span className="font-label-md text-label-md">Preview</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-surface-container-highest text-on-surface border border-outline-variant">1920x1080</span>
                  {lyrics.length > 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary border border-primary/30">Synced</span>}
                </div>
              </div>
              
              <div className="flex-1 bg-surface-container-lowest relative flex items-center justify-center p-4">
                <div className="aspect-video w-full max-w-4xl bg-black rounded shadow-lg border border-outline-variant relative overflow-hidden flex items-center justify-center group">
                  <CanvasPreview 
                    ref={canvasRef}
                    currentTime={currentTime}
                    bgMedia={bgMedia}
                    lyrics={lyrics}
                    styles={styles}
                    isPlaying={isPlaying}
                    width={1920}
                    height={1080}
                    analyserRef={analyserRef}
                  />
                  {isExporting && (
                    <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-xl font-bold text-white">Exporting Video...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Play Controls Overlay for Empty State or Hover */}
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 backdrop-blur-[2px]">
                      <button onClick={handlePlayPause} className="w-16 h-16 rounded-full bg-primary-container text-white flex items-center justify-center hover:scale-105 transition-transform shadow-xl">
                        <span className="material-symbols-outlined text-3xl" style={{fontVariationSettings: "'FILL' 1"}}>play_arrow</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Transport Controls */}
              <div className="h-12 border-t border-outline-variant bg-surface-container-low flex items-center justify-between px-4 shrink-0">
                <div className="flex items-center gap-3 text-on-surface-variant">
                  <button className="hover:text-on-surface transition-colors"><span className="material-symbols-outlined text-[20px]">skip_previous</span></button>
                  <button className="hover:text-on-surface transition-colors"><span className="material-symbols-outlined text-[20px]">fast_rewind</span></button>
                  <button onClick={handlePlayPause} className="w-8 h-8 rounded-full bg-on-surface text-surface flex items-center justify-center hover:scale-105 transition-transform">
                    <span className="material-symbols-outlined text-[20px]" style={{fontVariationSettings: "'FILL' 1"}}>{isPlaying ? 'pause' : 'play_arrow'}</span>
                  </button>
                  <button className="hover:text-on-surface transition-colors"><span className="material-symbols-outlined text-[20px]">fast_forward</span></button>
                  <button className="hover:text-on-surface transition-colors"><span className="material-symbols-outlined text-[20px]">skip_next</span></button>
                </div>
                <div className="font-mono-editor text-[13px] text-on-surface-variant flex items-center gap-2">
                  <span className={isPlaying ? "text-primary" : ""}>{formatTime(currentTime)}</span>
                  <span className="text-outline-variant">/</span>
                  <span className="text-outline">{formatTime(duration)}</span>
                </div>
              </div>
            </section>

            {/* Right Panel (Tabs for Script, Media, Style) */}
            <section className="flex-[2] bg-surface-container border border-outline-variant rounded-lg flex flex-col overflow-hidden shadow-sm">
              <div className="h-10 border-b border-outline-variant bg-surface-container-low flex items-center px-2 gap-1 shrink-0">
                <button 
                  onClick={() => setActiveTab('script')}
                  className={`px-3 py-1 rounded text-label-md flex items-center gap-1 transition-colors ${activeTab === 'script' ? 'bg-surface-container-highest text-on-surface' : 'text-on-surface-variant hover:text-on-surface'}`}
                >
                  <span className="material-symbols-outlined text-[16px]">subject</span> Script
                </button>
                <button 
                  onClick={() => setActiveTab('media')}
                  className={`px-3 py-1 rounded text-label-md flex items-center gap-1 transition-colors ${activeTab === 'media' ? 'bg-surface-container-highest text-on-surface' : 'text-on-surface-variant hover:text-on-surface'}`}
                >
                  <span className="material-symbols-outlined text-[16px]">perm_media</span> Media
                </button>
                <button 
                  onClick={() => setActiveTab('style')}
                  className={`px-3 py-1 rounded text-label-md flex items-center gap-1 transition-colors ${activeTab === 'style' ? 'bg-surface-container-highest text-on-surface' : 'text-on-surface-variant hover:text-on-surface'}`}
                >
                  <span className="material-symbols-outlined text-[16px]">palette</span> Style
                </button>
              </div>

              <div className="flex-1 overflow-y-auto bg-surface-container-lowest">
                {activeTab === 'script' && (
                  <div className="p-4 space-y-2">
                    {lyrics.length === 0 ? (
                      <div className="text-center p-8 text-on-surface-variant border border-dashed border-outline-variant rounded-lg m-4">
                        <span className="material-symbols-outlined text-4xl mb-2 opacity-50">format_align_left</span>
                        <p className="font-label-md">No lyrics yet</p>
                        <p className="text-sm opacity-70 mt-1">Go to the Media tab to import an .lrc file.</p>
                      </div>
                    ) : (
                      lyrics.map((line, idx) => {
                        const isActive = currentTime >= line.time && (idx === lyrics.length - 1 || currentTime < lyrics[idx + 1].time);
                        return (
                          <div key={idx} className={`group flex gap-3 p-2 rounded-md border transition-colors ${isActive ? 'bg-surface-container-highest border-primary/30 relative' : 'hover:bg-surface-container border-transparent hover:border-outline-variant cursor-pointer'}`}>
                            {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-md"></div>}
                            <div className={`w-16 shrink-0 pt-1 font-mono-editor text-[11px] text-right ${isActive ? 'text-primary font-medium' : 'text-outline'}`}>
                              {formatTime(line.time).substring(3,8)}
                            </div>
                            <div className={`flex-1 font-mono-editor text-[14px] leading-relaxed ${isActive ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                              {line.text}
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
                {activeTab === 'media' && (
                  <div className="p-4">
                    <MediaBin setAudioUrl={setAudioUrl} setBgMedia={setBgMedia} setLyrics={setLyrics} bgMedia={bgMedia} />
                  </div>
                )}
                {activeTab === 'style' && (
                  <div className="p-4">
                    <Inspector styles={styles} setStyles={setStyles} />
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Bottom Row: Audio Timeline */}
          <section className="h-48 bg-surface-container border border-outline-variant rounded-lg flex flex-col shrink-0 shadow-sm overflow-hidden">
            <div className="h-8 border-b border-outline-variant bg-surface-container-low flex items-center justify-between px-3 shrink-0">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined text-[16px]">graphic_eq</span>
                <span className="font-label-sm text-label-sm">Timeline</span>
              </div>
            </div>
            <div className="flex-1 relative bg-surface-container-lowest p-2 overflow-hidden flex flex-col">
              <Timeline 
                audioUrl={audioUrl}
                lyrics={lyrics}
                setLyrics={setLyrics}
                currentTime={currentTime}
                setCurrentTime={setCurrentTime}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                duration={duration}
                setDuration={setDuration}
                wavesurferRef={wavesurferRef}
                audioContextRef={audioContextRef}
                mediaElementSourceRef={mediaElementSourceRef}
                audioDestRef={audioDestRef}
                analyserRef={analyserRef}
                onPlayPause={handlePlayPause}
              />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
